import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Building2, Heart, Bell, Settings, Plus, Pencil, Trash2, Eye, Mail, MapPin, DollarSign, LogOut, Loader2, TrendingUp, BarChart3, ClipboardList, Bookmark, BellOff, Search, ExternalLink, Shield, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import MessagesPanel from '../components/MessagesPanel';
import { useAuth, API } from '../App';

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(price);
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout, updateUser } = useAuth();
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [loading, setLoading] = useState(true);
  const [myDeals, setMyDeals] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [myProvider, setMyProvider] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    bio: ''
  });

  // Provider form
  const [providerForm, setProviderForm] = useState({
    business_name: '',
    category: '',
    service_area: '',
    bio: '',
    phone: '',
    email: '',
    website: ''
  });
  const [savingProvider, setSavingProvider] = useState(false);

  const categories = [
    'Realtor', 'Contractor', 'Hard Money Lender', 'Private Lender',
    'Title Company', 'Inspector', 'Property Manager', 'Insurance Provider', 'Attorney'
  ];

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        bio: user.bio || ''
      });
      fetchData();
    }
  }, [user]);

  // Handle tab from URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dealsRes, favsRes, providerRes, inqRes, analyticsRes, onboardingRes, savedSearchesRes, unreadMsgRes] = await Promise.all([
        axios.get(`${API}/my/deals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my/provider`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inquiries/received`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my/analytics`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/onboarding`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/saved-searches`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { unread_count: 0 } }))
      ]);
      
      setMyDeals(dealsRes.data);
      setFavorites(favsRes.data);
      setMyProvider(providerRes.data);
      setInquiries(inqRes.data);
      setAnalytics(analyticsRes.data);
      setOnboardingData(onboardingRes.data);
      setSavedSearches(savedSearchesRes.data || []);
      setUnreadMessages(unreadMsgRes.data?.unread_count || 0);
      
      if (providerRes.data) {
        setProviderForm({
          business_name: providerRes.data.business_name || '',
          category: providerRes.data.category || '',
          service_area: providerRes.data.service_area || '',
          bio: providerRes.data.bio || '',
          phone: providerRes.data.phone || '',
          email: providerRes.data.email || '',
          website: providerRes.data.website || ''
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.put(`${API}/auth/me`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(response.data);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      await axios.delete(`${API}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyDeals(prev => prev.filter(d => d.deal_id !== dealId));
      toast.success('Deal deleted');
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const handleRemoveFavorite = async (dealId) => {
    try {
      await axios.post(`${API}/favorites`, { deal_id: dealId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => prev.filter(d => d.deal_id !== dealId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const handleSaveProvider = async () => {
    setSavingProvider(true);
    try {
      if (myProvider) {
        await axios.put(`${API}/providers/${myProvider.provider_id}`, providerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Provider profile updated');
      } else {
        const response = await axios.post(`${API}/providers`, providerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyProvider(response.data);
        toast.success('Provider profile created');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleDeleteProvider = async () => {
    try {
      await axios.delete(`${API}/providers/${myProvider.provider_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProvider(null);
      setProviderForm({
        business_name: '',
        category: '',
        service_area: '',
        bio: '',
        phone: '',
        email: '',
        website: ''
      });
      toast.success('Provider profile deleted');
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await axios.delete(`${API}/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedSearches(prev => prev.filter(s => s.search_id !== searchId));
      toast.success('Saved search deleted');
    } catch (error) {
      toast.error('Failed to delete saved search');
    }
  };

  const handleToggleSearchNotifications = async (searchId) => {
    try {
      const response = await axios.put(`${API}/saved-searches/${searchId}/toggle-notifications`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedSearches(prev => prev.map(s => 
        s.search_id === searchId ? { ...s, notify_new_deals: response.data.notify_new_deals } : s
      ));
      toast.success(response.data.notify_new_deals ? 'Notifications enabled' : 'Notifications disabled');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };

  const handleApplySavedSearch = async (search) => {
    // Mark as checked first
    try {
      await axios.put(`${API}/saved-searches/${search.search_id}/mark-checked`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail
    }

    // Build URL params from filters
    const params = new URLSearchParams();
    const filters = search.filters || {};
    if (filters.city) params.set('city', filters.city);
    if (filters.state) params.set('state', filters.state);
    if (filters.zip_code) params.set('zip', filters.zip_code);
    if (filters.property_type) params.set('type', filters.property_type);
    if (filters.min_price) params.set('min', filters.min_price);
    if (filters.max_price) params.set('max', filters.max_price);
    if (filters.bedrooms) params.set('beds', filters.bedrooms);
    if (filters.deal_type) params.set('deal_type', filters.deal_type);

    navigate(`/marketplace?${params.toString()}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-slate-600">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            {user?.role === 'admin' && (
              <Button asChild variant="outline" className="rounded-full border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="admin-panel-btn">
                <Link to="/admin">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Panel
                </Link>
              </Button>
            )}
            <Button asChild className="bg-blue-700 hover:bg-blue-800 rounded-full" data-testid="post-deal-btn">
              <Link to="/post-deal">
                <Plus className="h-4 w-4 mr-2" />
                Post a Deal
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#5DADE2]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{myDeals.length}</div>
                <div className="text-sm text-slate-500">My Deals</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Eye className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{analytics?.total_views || 0}</div>
                <div className="text-sm text-slate-500">Total Views</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <Heart className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{favorites.length}</div>
                <div className="text-sm text-slate-500">Saved</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{inquiries.filter(i => i.status === 'unread').length}</div>
                <div className="text-sm text-slate-500">New Inquiries</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <User className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900 capitalize">{user?.role}</div>
                <div className="text-sm text-slate-500">Role</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="deals" data-testid="tab-deals">My Deals</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="tab-favorites">Saved</TabsTrigger>
            <TabsTrigger value="searches" data-testid="tab-searches" className="relative">
              Saved Searches
              {savedSearches.reduce((acc, s) => acc + (s.new_deals_count || 0), 0) > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {savedSearches.reduce((acc, s) => acc + (s.new_deals_count || 0), 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="relative">
              Messages
              {unreadMessages > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#5DADE2] text-white text-xs rounded-full flex items-center justify-center">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="provider" data-testid="tab-provider">Provider</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Weekly engagement highlight (supplier engagement) */}
            {myDeals.length > 0 && analytics && (() => {
              const viewsLast7 = (analytics.deals || []).reduce((s, d) => s + (d.views_last_7_days || 0), 0);
              const activeDeals = (analytics.deals || []).filter(d => (d.views_last_7_days || 0) > 0);
              if (viewsLast7 === 0 && analytics.total_inquiries === 0) return null;
              return (
                <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50 p-5" data-testid="engagement-highlight">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                        <Eye className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">
                          Your deals got <span className="text-blue-700">{viewsLast7}</span> views this week
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          {activeDeals.length > 0
                            ? `${activeDeals.length} of your listings got investor attention · ${analytics.total_inquiries} total inquiries`
                            : `${analytics.total_inquiries} total inquiries received`}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setActiveTab('analytics')} data-testid="view-analytics-btn">
                      View analytics
                    </Button>
                  </div>
                </div>
              );
            })()}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Deals */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Recent Deals</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('deals')}>View All</Button>
                </div>
                {myDeals.length === 0 ? (
                  <p className="text-slate-500 text-sm">No deals posted yet</p>
                ) : (
                  <div className="space-y-3">
                    {myDeals.slice(0, 3).map(deal => (
                      <Link key={deal.deal_id} to={`/deals/${deal.deal_id}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                        <div className="w-12 h-12 bg-slate-200 rounded-lg overflow-hidden">
                          <img src={deal.photos?.[0] || 'https://via.placeholder.com/48'} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">{deal.title}</div>
                          <div className="text-sm text-slate-500">{formatPrice(deal.asking_price)}</div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Eye className="h-4 w-4" />
                            {deal.views_count || 0}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Inquiries */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Recent Inquiries</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('inquiries')}>View All</Button>
                </div>
                {inquiries.length === 0 ? (
                  <p className="text-slate-500 text-sm">No inquiries yet</p>
                ) : (
                  <div className="space-y-3">
                    {inquiries.slice(0, 3).map(inq => (
                      <div key={inq.inquiry_id} className={`p-3 rounded-lg ${inq.status === 'unread' ? 'bg-blue-50' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{inq.name}</span>
                          {inq.status === 'unread' && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-1">{inq.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                      <Eye className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-slate-900">{analytics?.total_views || 0}</div>
                      <div className="text-sm text-slate-500">Total Views</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Across all {analytics?.total_deals || 0} deals
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                      <Mail className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-slate-900">{analytics?.total_inquiries || 0}</div>
                      <div className="text-sm text-slate-500">Total Inquiries</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    From interested investors
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-slate-900">
                        {analytics?.total_views && analytics?.total_inquiries 
                          ? ((analytics.total_inquiries / analytics.total_views) * 100).toFixed(1) 
                          : '0'}%
                      </div>
                      <div className="text-sm text-slate-500">Conversion Rate</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Views to inquiries
                  </p>
                </div>
              </div>

              {/* Deal Performance Table */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <BarChart3 className="h-5 w-5 text-slate-600" />
                  <h3 className="font-semibold text-lg text-slate-900">Deal Performance</h3>
                </div>
                
                {!analytics?.deals || analytics.deals.length === 0 ? (
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h4 className="font-medium text-slate-900 mb-2">No analytics data yet</h4>
                    <p className="text-slate-500 mb-4">Post your first deal to start tracking performance</p>
                    <Button asChild className="rounded-full bg-[#5DADE2] hover:bg-[#4A9BD1]">
                      <Link to="/post-deal">Post a Deal</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Property</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Total Views</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Last 7 Days</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Inquiries</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Conv. Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.deals.map((deal, idx) => (
                          <tr key={deal.deal_id} className={idx % 2 === 0 ? 'bg-slate-50' : ''}>
                            <td className="py-3 px-4">
                              <Link to={`/deals/${deal.deal_id}`} className="font-medium text-slate-900 hover:text-[#5DADE2] truncate block max-w-xs">
                                {deal.title}
                              </Link>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-semibold text-slate-900">{deal.total_views}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-medium ${deal.views_last_7_days > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                                {deal.views_last_7_days > 0 ? '+' : ''}{deal.views_last_7_days}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-medium text-slate-900">{deal.inquiries}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                deal.total_views > 0 && deal.inquiries > 0 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {deal.total_views > 0 ? ((deal.inquiries / deal.total_views) * 100).toFixed(1) : '0'}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tips Section */}
              <div className="bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl border border-sky-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-3">Tips to Improve Performance</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DADE2] font-bold">•</span>
                    Add high-quality photos to increase views by up to 40%
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DADE2] font-bold">•</span>
                    Keep your pricing competitive based on ARV
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DADE2] font-bold">•</span>
                    Respond to inquiries within 24 hours for best results
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#5DADE2] font-bold">•</span>
                    Include detailed descriptions with rehab scope
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* My Deals Tab */}
          <TabsContent value="deals">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg text-slate-900">My Deals ({myDeals.length})</h3>
                <Button asChild className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full">
                  <Link to="/post-deal">
                    <Plus className="h-4 w-4 mr-2" />
                    Post New Deal
                  </Link>
                </Button>
              </div>
              
              {myDeals.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-900 mb-2">No deals yet</h4>
                  <p className="text-slate-500 mb-4">Post your first deal to start connecting with investors</p>
                  <Button asChild className="rounded-full bg-[#5DADE2] hover:bg-[#4A9BD1]">
                    <Link to="/post-deal">Post a Deal</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myDeals.map(deal => (
                    <div key={deal.deal_id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl" data-testid={`my-deal-${deal.deal_id}`}>
                      <div className="w-20 h-20 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={deal.photos?.[0] || 'https://via.placeholder.com/80'} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 mb-1">{deal.title}</div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {deal.city}, {deal.state}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {formatPrice(deal.asking_price)}
                          </span>
                          <span className="flex items-center gap-1 text-purple-600">
                            <Eye className="h-4 w-4" />
                            {deal.views_count || 0} views
                          </span>
                        </div>
                        <div className="mt-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${deal.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                            {deal.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" asChild data-testid={`edit-deal-${deal.deal_id}`}>
                          <Link to={`/edit-deal/${deal.deal_id}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/deals/${deal.deal_id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Deal?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete this deal. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteDeal(deal.deal_id)} className="bg-red-500 hover:bg-red-600">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-6">Saved Deals ({favorites.length})</h3>
              
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-900 mb-2">No saved deals</h4>
                  <p className="text-slate-500 mb-4">Browse the marketplace and save deals you're interested in</p>
                  <Button asChild className="rounded-full">
                    <Link to="/marketplace">Browse Deals</Link>
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favorites.map(deal => (
                    <div key={deal.deal_id} className="bg-slate-50 rounded-xl overflow-hidden" data-testid={`saved-deal-${deal.deal_id}`}>
                      <div className="aspect-video bg-slate-200 relative">
                        <img src={deal.photos?.[0] || 'https://via.placeholder.com/300x200'} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => handleRemoveFavorite(deal.deal_id)}
                          className="absolute top-2 right-2 p-1.5 bg-white rounded-full hover:bg-red-50 transition-colors"
                        >
                          <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                        </button>
                      </div>
                      <div className="p-4">
                        <div className="font-medium text-slate-900 mb-1 truncate">{deal.title}</div>
                        <div className="text-sm text-slate-500 mb-2">{deal.city}, {deal.state}</div>
                        <div className="text-lg font-bold text-slate-900 mb-3">{formatPrice(deal.asking_price)}</div>
                        <Button asChild className="w-full rounded-full" size="sm">
                          <Link to={`/deals/${deal.deal_id}`}>View Deal</Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-6">
                Inquiries ({inquiries.length})
              </h3>
              
              {inquiries.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-900 mb-2">No inquiries yet</h4>
                  <p className="text-slate-500">You'll receive inquiries when people are interested in your deals or services</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {inquiries.map(inq => (
                    <div key={inq.inquiry_id} className={`p-4 rounded-xl border ${inq.status === 'unread' ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-medium text-slate-900">{inq.name}</span>
                          <span className="text-slate-500 text-sm ml-2">
                            {new Date(inq.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${inq.target_type === 'deal' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {inq.target_type}
                        </span>
                      </div>
                      <p className="text-slate-600 mb-3">{inq.message}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <a href={`mailto:${inq.email}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                          <Mail className="h-4 w-4" />
                          {inq.email}
                        </a>
                        {inq.phone && (
                          <a href={`tel:${inq.phone}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                            <span>{inq.phone}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="mb-6">
                <h3 className="font-semibold text-lg text-slate-900">Messages</h3>
                <p className="text-sm text-slate-500">Your conversations with deal posters and service providers</p>
              </div>
              <MessagesPanel token={token} userId={user?.user_id} />
            </div>
          </TabsContent>

          {/* Saved Searches Tab */}
          <TabsContent value="searches">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Saved Searches ({savedSearches.length})</h3>
                  <p className="text-sm text-slate-500">Get notified when new deals match your criteria</p>
                </div>
                <div className="flex items-center gap-2">
                  {savedSearches.length > 0 && (
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/saved-searches/send-digest`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (res.data.sent) {
                            toast.success(`Digest sent with ${res.data.new_deals} new deals — check your inbox.`);
                          } else {
                            toast.info(res.data.reason === 'no new deals' ? 'No new matches since your last check.' : 'Digest not sent.');
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Could not send digest');
                        }
                      }}
                      data-testid="send-my-digest-btn"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email me my matches
                    </Button>
                  )}
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/marketplace">
                      <Search className="h-4 w-4 mr-2" />
                      New Search
                    </Link>
                  </Button>
                </div>
              </div>
              
              {savedSearches.length === 0 ? (
                <div className="text-center py-12">
                  <Bookmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-medium text-slate-900 mb-2">No saved searches</h4>
                  <p className="text-slate-500 mb-4">Save searches from the marketplace to get notified of new matching deals</p>
                  <Button asChild className="rounded-full">
                    <Link to="/marketplace">Browse Deals</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedSearches.map(search => (
                    <div 
                      key={search.search_id} 
                      className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
                      data-testid={`saved-search-${search.search_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-slate-900 flex items-center gap-2">
                            {search.name}
                            {search.new_deals_count > 0 && (
                              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                {search.new_deals_count} new
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-500 mt-1">
                            Saved {new Date(search.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleSearchNotifications(search.search_id)}
                            className={`p-2 rounded-full transition-colors ${
                              search.notify_new_deals 
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                            }`}
                            title={search.notify_new_deals ? 'Notifications on' : 'Notifications off'}
                            data-testid={`toggle-notify-${search.search_id}`}
                          >
                            {search.notify_new_deals ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Saved Search?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete this saved search and you won't receive notifications for it anymore.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteSavedSearch(search.search_id)} 
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>

                      {/* Filter Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {search.filters?.city && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            City: {search.filters.city}
                          </span>
                        )}
                        {search.filters?.state && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            State: {search.filters.state}
                          </span>
                        )}
                        {search.filters?.zip_code && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            ZIP: {search.filters.zip_code}
                          </span>
                        )}
                        {search.filters?.property_type && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {search.filters.property_type}
                          </span>
                        )}
                        {search.filters?.min_price && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            Min: ${parseInt(search.filters.min_price).toLocaleString()}
                          </span>
                        )}
                        {search.filters?.max_price && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            Max: ${parseInt(search.filters.max_price).toLocaleString()}
                          </span>
                        )}
                        {search.filters?.bedrooms && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {search.filters.bedrooms}+ Beds
                          </span>
                        )}
                        {search.filters?.deal_type && (
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded capitalize">
                            {search.filters.deal_type.replace('_', ' ')}
                          </span>
                        )}
                      </div>

                      <Button 
                        onClick={() => handleApplySavedSearch(search)}
                        className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full"
                        data-testid={`apply-search-${search.search_id}`}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Matching Deals
                        {search.new_deals_count > 0 && ` (${search.new_deals_count} new)`}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Provider Tab */}
          <TabsContent value="provider">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-6">
                {myProvider ? 'Edit Provider Profile' : 'Create Provider Profile'}
              </h3>
              
              <div className="space-y-6 max-w-2xl">
                <div>
                  <Label htmlFor="business_name">Business Name</Label>
                  <Input
                    id="business_name"
                    value={providerForm.business_name}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, business_name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={providerForm.category} onValueChange={(v) => setProviderForm(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="service_area">Service Area</Label>
                    <Input
                      id="service_area"
                      value={providerForm.service_area}
                      onChange={(e) => setProviderForm(prev => ({ ...prev, service_area: e.target.value }))}
                      placeholder="e.g., Austin, TX Metro Area"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="provider_bio">Bio / Description</Label>
                  <Textarea
                    id="provider_bio"
                    value={providerForm.bio}
                    onChange={(e) => setProviderForm(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="provider_phone">Phone</Label>
                    <Input
                      id="provider_phone"
                      value={providerForm.phone}
                      onChange={(e) => setProviderForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="provider_email">Email</Label>
                    <Input
                      id="provider_email"
                      type="email"
                      value={providerForm.email}
                      onChange={(e) => setProviderForm(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="provider_website">Website (optional)</Label>
                    <Input
                      id="provider_website"
                      value={providerForm.website}
                      onChange={(e) => setProviderForm(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSaveProvider} disabled={savingProvider} className="bg-blue-700 hover:bg-blue-800 rounded-full">
                    {savingProvider ? 'Saving...' : myProvider ? 'Update Profile' : 'Create Profile'}
                  </Button>
                  {myProvider && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 rounded-full">
                          Delete Profile
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Provider Profile?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete your provider listing from the directory.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDeleteProvider} className="bg-red-500 hover:bg-red-600">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-lg text-slate-900 mb-6">Account Settings</h3>
              
              <div className="space-y-6 max-w-md">
                <div>
                  <Label htmlFor="profile_name">Name</Label>
                  <Input
                    id="profile_name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="mt-1 bg-slate-50" />
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <Label htmlFor="profile_phone">Phone</Label>
                  <Input
                    id="profile_phone"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="profile_bio">Bio</Label>
                  <Textarea
                    id="profile_bio"
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <Button onClick={handleProfileSave} disabled={savingProfile} className="bg-blue-700 hover:bg-blue-800 rounded-full">
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>

              <div className="border-t border-slate-200 mt-8 pt-8">
                <h4 className="font-medium text-slate-900 mb-4">Investment Preferences</h4>
                <div className="bg-slate-50 rounded-xl p-4 mb-4">
                  {onboardingData ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Onboarding Status</span>
                        <span className="text-sm font-medium text-green-600">Complete</span>
                      </div>
                      {user?.role === 'investor' && onboardingData.investor && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Target Markets</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.investor.target_markets?.length || 0} locations
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Deal Types</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.investor.deal_types?.join(', ') || 'Not set'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Price Range</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.investor.purchase_price_range || 'Not set'}
                            </span>
                          </div>
                        </>
                      )}
                      {user?.role === 'wholesaler' && onboardingData.wholesaler && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Deal Markets</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.wholesaler.deal_markets?.length || 0} locations
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Deals/Month</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.wholesaler.deals_per_month || 'Not set'}
                            </span>
                          </div>
                        </>
                      )}
                      {user?.role === 'provider' && onboardingData.provider && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Service Type</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.provider.service_type || 'Not set'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">Markets Served</span>
                            <span className="text-sm text-slate-900">
                              {onboardingData.provider.markets_served?.length || 0} locations
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Onboarding not complete</p>
                        <p className="text-xs text-slate-500">Tell us about your investment preferences</p>
                      </div>
                    </div>
                  )}
                </div>
                <Button asChild variant="outline" className="rounded-full" data-testid="edit-onboarding-btn">
                  <Link to="/onboarding">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    {onboardingData ? 'Edit Preferences' : 'Complete Onboarding'}
                  </Link>
                </Button>
              </div>

              <div className="border-t border-slate-200 mt-8 pt-8">
                <h4 className="font-medium text-slate-900 mb-4">Danger Zone</h4>
                <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-200 hover:bg-red-50 rounded-full">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
